import { memo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import { FeatureFlagAvailabilitiesDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { CommunitiesDropdown } from 'modules/dashboard/components/Groups/types'
import PinnedPostsList from 'modules/posts/components/PinnedPostsList'

const POST_PINNING_FEATURE_FLAG = 'dev_post_pinning'

type DashboardPinnedPostsProps = {
  selectedCommunity: CommunitiesDropdown
}

const groupsFromCommunity = (community: CommunitiesDropdown): string[] | undefined => {
  if (community.strategy === 'group' && community.value) {
    return [String(community.value)]
  }
  return undefined
}

const DashboardPinnedPosts = ({ selectedCommunity }: DashboardPinnedPostsProps) => {
  const { company } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const { data: featureFlagData } = useQuery(FeatureFlagAvailabilitiesDocument, {
    fetchPolicy: 'cache-first',
    variables: {
      companyId: company.id,
      featureNames: [POST_PINNING_FEATURE_FLAG],
    },
  })

  const isPostPinningEnabled =
    featureFlagData?.features?.featuresConnection?.nodes?.find(
      (feature) => feature.name === POST_PINNING_FEATURE_FLAG
    )?.enabled ?? false

  const groups = groupsFromCommunity(selectedCommunity)

  const getPostHref = useCallback(
    (postId: string) => navigation.dashboard.post.id.fullPath({ id: postId }),
    [navigation]
  )

  const handlePostClick = useCallback(
    (postId: string) => {
      navigate(navigation.dashboard.post.id.fullPath({ id: postId }))
    },
    [navigate, navigation]
  )

  return (
    <PinnedPostsList
      groups={groups}
      isPostPinningEnabled={isPostPinningEnabled}
      getPostHref={getPostHref}
      onPostClick={handlePostClick}
    />
  )
}

export default memo(DashboardPinnedPosts)
